/**
 * A capability read-out for the device the prototype is running on.
 *
 * This exists because section 7 asks us to compare results across a MacBook, a
 * phone, and the actual IFP - and "it was slower on the panel" is only useful
 * if we also recorded what the panel actually is. `maxTouchPoints` in
 * particular answers the section 3 question "can several students interact at
 * once?" before we build anything that depends on the answer.
 */

export interface DeviceInfo {
  screen: string;
  viewport: string;
  devicePixelRatio: number;
  maxTouchPoints: number;
  graphics: string;
  renderer: string;
  standalone: boolean;
  userAgent: string;
}

function probeGraphics(): { graphics: string; renderer: string } {
  const canvas = document.createElement('canvas');

  const gl =
    (canvas.getContext('webgl2') as WebGL2RenderingContext | null) ??
    (canvas.getContext('webgl') as WebGLRenderingContext | null);

  if (!gl) return { graphics: 'none', renderer: 'unavailable' };

  const api = 'drawingBufferColorSpace' in gl && 'texStorage2D' in gl ? 'WebGL 2' : 'WebGL 1';

  // Some browsers mask this for fingerprinting reasons; fall back to the
  // generic RENDERER string, which is still better than nothing.
  let renderer = 'masked';
  const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
  if (debugInfo) {
    renderer = String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL));
  } else {
    renderer = String(gl.getParameter(gl.RENDERER));
  }

  const webgpu = 'gpu' in navigator ? ' (WebGPU available)' : '';
  return { graphics: api + webgpu, renderer };
}

export function readDeviceInfo(): DeviceInfo {
  const { graphics, renderer } = probeGraphics();

  return {
    screen: `${window.screen.width} × ${window.screen.height}`,
    viewport: `${window.innerWidth} × ${window.innerHeight}`,
    devicePixelRatio: window.devicePixelRatio || 1,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    graphics,
    renderer,
    // True when launched from a home-screen icon rather than a browser tab -
    // this is how we will verify the section 6 install experiments later.
    standalone: window.matchMedia('(display-mode: standalone)').matches,
    userAgent: navigator.userAgent,
  };
}
